import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, Phone, MessageCircle } from "lucide-react";
import { SERVICES, COMPANY } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";

/**
 * SEO-targeted per-service subpages:
 *  /services/joinery
 *  /services/upholstery
 *  /services/mep
 *  (and any other slug present in SERVICES)
 */

const SERVICE_META = {
  joinery: {
    title: "Joinery Company Dubai | Custom Furniture UAE — Time Furniture",
    description:
      "Joinery company Dubai trusts — bespoke wardrobes, kitchens, TV units, wall paneling and custom furniture UAE, manufactured in our own Al Sajja factory.",
    h1Eyebrow: "Custom Joinery & Furniture",
    h1: "Joinery Company Dubai — built in our own factory.",
  },
  upholstery: {
    title: "Upholstery Dubai | Sofa & Wall Panel Upholstery UAE — Time Furniture",
    description:
      "Upholstery Dubai specialists — sofas, chairs, beds, headboards and wall panels upholstered in our own Sharjah atelier. Bespoke fabric selection & frame craftsmanship.",
    h1Eyebrow: "In-House Upholstery",
    h1: "Upholstery Dubai — sofas, headboards, wall panels.",
  },
  mep: {
    title: "MEP Contractor Dubai | HVAC, Electrical & Plumbing UAE — Time Furniture",
    description:
      "MEP contractor Dubai — HVAC, electrical distribution, plumbing, low-voltage and BMS — coordinated with full interior scope for villas, offices and clinics in the UAE.",
    h1Eyebrow: "MEP — Mechanical, Electrical, Plumbing",
    h1: "MEP Contractor Dubai — systems behind the design.",
  },
};

export default function ServiceDetail() {
  const { key } = useParams();
  const service = useMemo(() => SERVICES.find((s) => s.key === key), [key]);

  const meta = service
    ? SERVICE_META[key] || {
        title: `${service.title} | Time Furniture UAE`,
        description: service.description,
        h1Eyebrow: service.title,
        h1: service.title,
      }
    : { title: "Service | Time Furniture UAE", description: undefined };

  usePageMeta({
    title: meta.title,
    description: meta.description,
    canonical: service
      ? `https://timefurniture.ae/services/${key}`
      : undefined,
    ogImage: service?.image,
    jsonLd: service
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: service.description,
          url: `https://timefurniture.ae/services/${key}`,
          image: service.image
            ? `https://timefurniture.ae${service.image}`
            : undefined,
          provider: { "@id": "https://timefurniture.ae/#organization" },
          areaServed: ["Dubai", "Sharjah", "United Arab Emirates"],
        }
      : undefined,
  });

  // If service key isn't in the catalog, send them to the main services page.
  if (!service) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-detail-${key}`} className="bg-[#FAF5EC]">
      <Breadcrumbs
        testid="service-detail-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: service.title },
        ]}
      />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-20 md:pb-28">
        <p className="eyebrow mb-6">{meta.h1Eyebrow}</p>
        <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl text-[#1C110C]">
          {meta.h1}
        </h1>
        <p className="mt-8 max-w-2xl text-[#3E2A22] leading-relaxed text-base md:text-lg">
          {service.description}
        </p>
      </section>

      {/* Image + points */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="overflow-hidden h-[420px] md:h-[560px] img-zoom-parent">
              <img loading="lazy"
                src={service.image}
                alt={service.imageAlt || `${service.title} by Time Furniture in Dubai & Sharjah`}
                className="w-full h-full object-cover img-zoom"
              />
            </div>
            {service.imageCaption && (
              <p className="mt-4 text-[11px] tracking-[0.26em] uppercase text-[#7A6B5F] italic">
                {service.imageCaption}
              </p>
            )}
          </div>

          <div className="lg:col-span-5 lg:pl-6">
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C] italic mb-5">
              {service.tagline}
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-[1.1] text-[#1C110C] mb-8">
              What&rsquo;s included
            </h2>
            <ul className="space-y-4">
              {service.points.map((p) => (
                <li key={p} className="flex gap-4 items-start">
                  <Check size={16} className="mt-1 text-[#6E1F2C] shrink-0" strokeWidth={2.4} />
                  <span className="text-[#1C110C] text-sm md:text-base">{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-[#6E1F2C]/15">
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-5">
                Explore other disciplines
              </p>
              <div className="flex flex-wrap gap-2">
                {SERVICES.filter((s) => s.key !== key)
                  .slice(0, 6)
                  .map((s) => (
                    <Link
                      key={s.key}
                      to={`/services/${s.key}`}
                      data-testid={`related-service-${s.key}`}
                      className="text-[11px] tracking-[0.24em] uppercase border border-[#6E1F2C]/25 text-[#3E2A22] px-4 py-2 hover:border-[#6E1F2C] hover:text-[#6E1F2C] transition-colors"
                    >
                      {s.title.split(" — ")[0]}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F2E9D8] border-t border-[#6E1F2C]/15">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="eyebrow mb-5">Start a Project</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl mx-auto">
            Ready to discuss your {service.title.toLowerCase()} project?
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${
                key === "curtains-soft"
                  ? COMPANY.whatsappCurtainsText
                  : COMPANY.whatsappFitOutText
              }`}
              target="_blank"
              rel="noreferrer"
              data-testid={`service-detail-whatsapp-${key}`}
              className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-all"
            >
              <MessageCircle size={16} strokeWidth={2.2} /> WhatsApp Now
            </a>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid={`service-detail-call-${key}`}
              className="inline-flex items-center gap-3 border border-[#6E1F2C]/60 text-[#6E1F2C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-[#EDE1C8] transition-all"
            >
              <Phone size={16} strokeWidth={2.2} /> Call Us
            </a>
            <Link
              to="/contact"
              data-testid={`service-detail-contact-${key}`}
              className="inline-flex items-center gap-3 text-[#6E1F2C] px-6 py-5 text-[11px] tracking-[0.32em] uppercase hover:text-[#56151F] transition-all"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
